import React, { PropsWithChildren, useId } from 'react';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';

import { bg1, bg2 } from '@osmosis/tailwind-ui/styles/background';
import { text3 } from '@osmosis/tailwind-ui/styles/text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons/faAngleDown';
import { faAngleUp } from '@fortawesome/pro-solid-svg-icons/faAngleUp';

import { ModalHookProps } from '../../hooks/useModal';

const toggleClass = `w-full justify-between rounded-[5px] p-2 text-left font-normal transition-all duration-100 ease-in-out ${bg1} hover:${bg2}`;

type Props = PropsWithChildren & {
  modalProps: ModalHookProps;
  name: string;
};

/**
 * Dropdown wrapper for the topics item in mobile view.
 */
const TopicsItemDropdownWrapper = (props: Props) => {
  const { children, modalProps, name } = props;
  const { setIsVisible, isVisible } = modalProps;
  const panelId = useId();

  return (
    <Box className="py-2 text-blue-100">
      <Button
        variant="transparent"
        color="transparent"
        data-testid="topics-item-dropdown-wrapper"
        aria-expanded={isVisible}
        aria-controls={panelId}
        onClick={() => setIsVisible(!isVisible)}
        className={toggleClass}
      >
        <span className="text-sm font-bold text-slate-800 dark:text-slate-100 sm:text-base">
          {name}
        </span>
        <Icon
          className={`text-sm font-bold ${text3}`}
          icon={isVisible ? faAngleUp : faAngleDown}
        />
      </Button>
      <AnimatePresence>
        <motion.div
          id={panelId}
          // Collapsed links must not stay tabbable or readable
          inert={!isVisible}
          style={{ overflow: 'hidden' }}
          transition={{ duration: 0.5 }}
          initial={{ height: 0 }}
          animate={{
            height: isVisible ? 'auto' : 0,
          }}
        >
          <Box className="overflow-y-scroll overflow-x-hidden w-full max-h-[432px]">
            {children}
          </Box>
        </motion.div>
      </AnimatePresence>
    </Box>
  );
};

export default TopicsItemDropdownWrapper;
